import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaCopy,
  FaEye,
  FaFileAlt,
  FaCheckCircle,
  FaLayerGroup,
  FaArchive,
  FaBoxOpen,
} from 'react-icons/fa';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import Spinner from '../components/common/Spinner';
import EmptyState from '../components/common/EmptyState';
import NextStepNudge from '../components/common/NextStepNudge';
import AddToPrintQueueButton from '../components/qr/AddToPrintQueueButton';
import { FormTemplate, FormStatus } from '../types';
import { useLabel } from '../i18n/useLabel';
import { toast } from '../utils/toast';
import {
  useForms,
  useDeleteForm,
  useDuplicateForm,
  usePublishForm,
  useArchiveForm,
  useUnarchiveForm,
  usePrefetchForm,
} from '../dry/hooks';

const Forms: React.FC = () => {
  const navigate = useNavigate();
  const labelSingular = useLabel('form');
  const labelPlural = useLabel('form', { form: 'plural' });
  const labelLowerPlural = useLabel('form', { form: 'plural', case: 'lower' });

  // Local UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<FormStatus | 'all'>('published'); // Default to published forms

  // React Query hooks for data fetching
  const { data: formsData, isLoading } = useForms({
    status: filterStatus !== 'all' ? filterStatus : undefined,
    limit: 100,
  });

  // Mutation hooks
  const deleteFormMutation = useDeleteForm();
  const duplicateFormMutation = useDuplicateForm();
  const publishFormMutation = usePublishForm();
  const archiveFormMutation = useArchiveForm();
  const unarchiveFormMutation = useUnarchiveForm();
  const prefetchForm = usePrefetchForm();

  // Extract data with defaults (useForms already returns a typed array)
  const forms = Array.isArray(formsData) ? formsData : [];

  const handleDelete = (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    deleteFormMutation.mutate(id);
  };

  const handleDuplicate = (id: string, name: string) => {
    duplicateFormMutation.mutate(id);
  };

  const handlePublish = (id: string) => {
    publishFormMutation.mutate(id);
  };

  const handleArchive = (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to archive "${name}"?`)) return;
    archiveFormMutation.mutate(id);
  };

  const handleUnarchive = (id: string, name: string) => {
    if (!window.confirm(`Unarchive "${name}" and restore it to draft status?`)) return;
    unarchiveFormMutation.mutate(id);
  };

  const filteredForms = (forms || []).filter(
    (form) =>
      !searchQuery ||
      form.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      form.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status: FormStatus): 'default' | 'success' | 'warning' => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <NextStepNudge forKey="form" />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{labelPlural}</h1>
          <p className="text-gray-600 mt-1">Create and manage your custom {labelLowerPlural}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => navigate('/templates')}>
            <FaLayerGroup className="mr-2" />
            Browse Templates
          </Button>
          <Button onClick={() => navigate('/forms/new')}>
            <FaPlus className="mr-2" />
            New {labelSingular}
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search forms..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as FormStatus | 'all')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            <option value="all">Show All</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </Card>

      {/* Results Count */}
      <p className="text-sm text-gray-600">
        Showing {filteredForms?.length || 0} of {forms?.length || 0} forms
      </p>

      {/* Forms List */}
      {filteredForms.length === 0 ? (
        searchQuery || filterStatus !== 'all' ? (
          <Card>
            <div className="text-center py-12">
              <FaFileAlt className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No {labelLowerPlural} match</h3>
              <p className="mt-2 text-sm text-gray-600">Try adjusting your search or filters</p>
            </div>
          </Card>
        ) : (
          <EmptyState
            icon={<FaFileAlt />}
            title="Build your first check"
            description={`${labelPlural} are the checklists your team fills out — questions, photos, signatures, conditional logic. Pick from the template library to get started fast, or build from scratch.`}
            primaryAction={{
              label: `Create ${labelSingular}`,
              onClick: () => navigate('/forms/new'),
            }}
            secondaryAction={{
              label: 'Browse Templates',
              onClick: () => navigate('/templates'),
            }}
            nextStep={{ label: 'After creating forms, set up schedules', to: '/schedules' }}
          />
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredForms.map((form) => (
            <Card
              key={form.id}
              className="hover:shadow-xl transition-shadow cursor-pointer group h-full"
              onClick={() => navigate(`/forms/${form.id}/edit`)}
              onMouseEnter={() => prefetchForm(form.id)}
            >
              <div className="space-y-4 flex flex-col h-full">
                {/* Form Icon */}
                {(() => {
                  const colors = [
                    'from-purple-50 to-pink-100 text-purple-600',
                    'from-blue-50 to-cyan-100 text-blue-600',
                    'from-green-50 to-emerald-100 text-green-600',
                    'from-orange-50 to-amber-100 text-orange-600',
                    'from-red-50 to-rose-100 text-red-600',
                    'from-teal-50 to-cyan-100 text-teal-600',
                    'from-indigo-50 to-violet-100 text-indigo-600',
                  ];
                  const colorIdx = (form.name || '').split('').reduce((acc: number, c: string) => acc + c.charCodeAt(0), 0) % colors.length;
                  const initials = (form.name || 'F').split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
                  return (
                    <div className={`aspect-video bg-linear-to-br ${colors[colorIdx]} rounded-lg flex items-center justify-center`}>
                      <span className="text-3xl font-bold">{initials}</span>
                    </div>
                  );
                })()}

                {/* Form Info */}
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-lg font-semibold text-gray-900 truncate flex-1">
                      {form.name}
                    </h3>
                    <Badge variant={getStatusColor(form.status)}>{form.status}</Badge>
                  </div>
                  {form.description && (
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{form.description}</p>
                  )}
                </div>

                {/* Metadata */}
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">{form.fields?.length || 0}</span> fields
                  </div>
                  <div>v{form.version}</div>
                  {form.category && <Badge variant="info">{form.category}</Badge>}
                </div>

                {/* Tags */}
                {form.tags && form.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {form.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-sm"
                      >
                        {tag}
                      </span>
                    ))}
                    {form.tags.length > 3 && (
                      <span className="px-2 py-0.5 text-xs text-gray-500">
                        +{form.tags.length - 3} more
                      </span>
                    )}
                  </div>
                )}

                {/* Quick Actions */}
                <div className="grid grid-cols-6 gap-1 opacity-0 group-hover:opacity-100 transition-opacity mt-auto">
                  {form.status !== 'archived' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/forms/${form.id}/edit`);
                      }}
                      className="px-2 py-2 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                    >
                      <FaEdit />
                      <span className="hidden xl:inline">Edit</span>
                    </button>
                  )}
                  {form.status !== 'archived' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(form.id, form.name);
                      }}
                      className="px-2 py-2 text-sm bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-1"
                    >
                      <FaCopy />
                      <span className="hidden xl:inline">Copy</span>
                    </button>
                  )}
                  {form.status === 'draft' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePublish(form.id);
                      }}
                      className="px-2 py-2 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center gap-1"
                    >
                      <FaCheckCircle />
                      <span className="hidden xl:inline">Publish</span>
                    </button>
                  )}
                  {form.status === 'archived' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUnarchive(form.id, form.name);
                      }}
                      className="px-2 py-2 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center gap-1"
                    >
                      <FaBoxOpen />
                      <span className="hidden xl:inline">Unarchive</span>
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleArchive(form.id, form.name);
                      }}
                      className="px-2 py-2 text-sm bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 transition-colors flex items-center justify-center gap-1"
                    >
                      <FaArchive />
                      <span className="hidden xl:inline">Archive</span>
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(form.id, form.name);
                    }}
                    className="px-2 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-1"
                  >
                    <FaTrash />
                    <span className="hidden xl:inline">Delete</span>
                  </button>
                  <AddToPrintQueueButton
                    target={{ target_kind: 'form', form_template_id: form.id }}
                    label={form.name}
                    className="px-2 py-2 text-sm bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors flex items-center justify-center gap-1 border-0"
                  />
                </div>

                {/* Updated date */}
                <p className="text-xs text-gray-500">
                  Updated {new Date(form.updated_at).toLocaleDateString()}
                </p>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Forms;
